import React, { useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useReactToPrint } from 'react-to-print'
import { fetchResells, deleteResell } from '../store/slices/resellSlice'
import * as api from '../api'
import { useSnackbar } from 'notistack'
import {
  X,
  ArrowUp,
  ArrowDown,
  Printer,
  Trash2,
  Download,
  Home,
  Ruler,
  User,
  DollarSign,
  Calendar,
  FileText
} from 'lucide-react'
import Logo from '../assets/Logo/Logo.jpg'
import * as XLSX from 'xlsx'
import Swal from 'sweetalert2'
import { useSidebarContext } from '../scenes/global/sidebar/SidebarContext'

const ResellPlotsList = () => {
  const dispatch = useDispatch()
  const { enqueueSnackbar } = useSnackbar()
  const { resells } = useSelector(state => state.resell)
  const [selectedResell, setSelectedResell] = useState(null)
  const [printType, setPrintType] = useState(null) // 'seller', 'purchaser', or 'full'
  const [sortColumn, setSortColumn] = useState(null)
  const [sortDirection, setSortDirection] = useState(null)
  const { activeSociety, theme, gradient } = useSidebarContext()
  const { user, activeUser } = useSelector(state => state.auth)
  const printRef = useRef()
  const sellerPrintRef = useRef()
  const purchaserPrintRef = useRef()

  useEffect(() => {
    dispatch(fetchResells())
  }, [dispatch])
  const isEmployee = activeUser?.role === 'Employee'

  const filteredResells = resells.filter(resell => {
    const isSameSociety = resell.plot_id?.society_id?._id === activeSociety?._id
    if (user?.role === 'Customer') {
      return (
        isSameSociety &&
        (resell.previous_customer_id?._id === user._id ||
          resell.new_customer_id?._id === user._id)
      )
    }
    return isSameSociety
  })

  const handleDelete = id => {
    if (isEmployee) return
    Swal.fire({
      title: 'Are you sure?',
      text: 'This action cannot be undone.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, delete it!',
      cancelButtonText: 'Cancel',
      background: '#1E293B',
      customClass: {
        popup: `bg-gradient-to-br from-slate-800 via-slate-900 to-slate-800 rounded-xl ${theme.tailwind.text}`,
        confirmButton:
          'bg-emerald-500 hover:bg-emerald-600 text-white px-6 py-2.5 rounded-lg transition-colors duration-200',
        cancelButton:
          'bg-red-500 hover:bg-red-600 text-white px-6 py-2.5 rounded-lg transition-colors duration-200',
        title: `text-2xl font-bold ${theme.tailwind.text9} mb-4`,
        container: 'backdrop-blur-sm'
      }
    }).then(result => {
      if (result.isConfirmed) {
        dispatch(deleteResell(id))

        Swal.fire({
          title: 'Deleted!',
          text: 'The transaction has been deleted.',
          icon: 'success',
          confirmButtonColor: '#10B981',
          background: '#1E293B',
          color: '#5EEAD4',
          confirmButtonText: 'Done',
          customClass: {
            popup:
              'bg-gradient-to-br from-slate-800 via-slate-900 to-slate-800 rounded-xl shadow-xl',
            confirmButton:
              'hover:bg-emerald-600 transition-colors px-6 py-2.5 rounded-lg',
            title: `text-xl font-bold ${theme.tailwind.text9}`
          }
        })
      }
    })
  }

  const handleViewSlip = resell => {
    setSelectedResell(resell)
    setPrintType('full')
  }

  const handlePrintSlip = useReactToPrint({
    contentRef: printRef,
    documentTitle: 'Al-Noor Developers - Resell Plot Certificate',
    pageStyle: `
    @page {
      size: A4;
      margin: 0;
    }
    @media print {
      body {
        -webkit-print-color-adjust: exact !important;
        print-color-adjust: exact !important;
      }
    }
  `
  })

  const handlePrintSellerSlip = useReactToPrint({
    contentRef: sellerPrintRef,
    documentTitle: 'Al-Noor Developers - Seller Fee Certificate',
    pageStyle: `
    @page {
      size: A4;
      margin: 0;
    }
    @media print {
      body {
        -webkit-print-color-adjust: exact !important;
        print-color-adjust: exact !important;
      }
    }
  `
  })

  const handlePrintPurchaserSlip = useReactToPrint({
    contentRef: purchaserPrintRef,
    documentTitle: 'Al-Noor Developers - Purchaser Fee Certificate',
    pageStyle: `
    @page {
      size: A4;
      margin: 0;
    }
    @media print {
      body {
        -webkit-print-color-adjust: exact !important;
        print-color-adjust: exact !important;
      }
    }
  `
  })

  const handleSort = column => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')
    } else {
      setSortColumn(column)
      setSortDirection('asc')
    }
  }

  const sortedResells = sortColumn
    ? [...resells].sort((a, b) => {
      const aValue = getValueByPath(a, sortColumn)
      const bValue = getValueByPath(b, sortColumn)
      return sortDirection === 'asc'
        ? String(aValue).localeCompare(String(bValue))
        : String(bValue).localeCompare(String(aValue))
    })
    : resells

  const getValueByPath = (obj, path) => {
    return path.split('.').reduce((o, p) => (o ? o[p] : null), obj)
  }

  const filterTransactionsByPeriod = (transactions, period) => {
    const currentDate = new Date()

    return transactions.filter(transaction => {
      const transactionDate = new Date(transaction.resell_date)

      switch (period) {
        case 'day':
          return isSameDay(transactionDate, currentDate)
        case 'week':
          return isSameWeek(transactionDate, currentDate)
        case 'month':
          return isSameMonth(transactionDate, currentDate)
        case 'year':
          return isSameYear(transactionDate, currentDate)
        case 'all':
        default:
          return true // No filter, show all
      }
    })
  }

  // Helper functions for date comparisons

  const isSameDay = (date1, date2) => {
    return (
      date1.getDate() === date2.getDate() &&
      date1.getMonth() === date2.getMonth() &&
      date1.getFullYear() === date2.getFullYear()
    )
  }

  const isSameWeek = (date1, date2) => {
    const startOfWeek = getStartOfWeek(date2)
    const endOfWeek = getEndOfWeek(date2)
    return date1 >= startOfWeek && date1 <= endOfWeek
  }

  const isSameMonth = (date1, date2) => {
    return (
      date1.getMonth() === date2.getMonth() &&
      date1.getFullYear() === date2.getFullYear()
    )
  }

  const isSameYear = (date1, date2) => {
    return date1.getFullYear() === date2.getFullYear()
  }

  const getStartOfWeek = date => {
    const startDate = new Date(date)
    startDate.setDate(date.getDate() - date.getDay()) // Set to the start of the week (Sunday)
    return startDate
  }

  const getEndOfWeek = date => {
    const endDate = new Date(date)
    endDate.setDate(date.getDate() + (6 - date.getDay())) // Set to the end of the week (Saturday)
    return endDate
  }

  const exportResellsToExcel = (resells, period) => {
    // Map the resell data to the structure you need for export
    const dataToExport = resells.map(resell => ({
      'Plot ID': resell.plot_id?.plot_number || 'N/A',
      'Previous Customer': resell.previous_customer_id?.name || 'N/A',
      'New Customer': resell.new_customer_id?.name || 'N/A',
      'Seller Fee': resell.seller_fee_amount ? `PKR ${resell.seller_fee_amount}` : 'N/A',
      'Purchaser Fee': resell.purchaser_fee_amount ? `PKR ${resell.purchaser_fee_amount}` : 'N/A',
      'Total Fee': resell.resell_fee ? `PKR ${resell.resell_fee}` : 'N/A',
      Society: resell.plot_id?.society_id?.name || 'N/A',
      'Resell Date': new Date(resell.resell_date || 'N/A').toLocaleDateString()
    }))

    // Create a worksheet from the data
    const worksheet = XLSX.utils.json_to_sheet(dataToExport)

    // Create a workbook and append the worksheet
    const workbook = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Resell History')

    // Set the filename based on the selected period
    const fileName = `resell_history_${period}.xlsx`

    // Write the Excel file to disk
    XLSX.writeFile(workbook, fileName)
  }

  const handleExportToExcel = () => {
    if (filteredResells.length === 0) return
    Swal.fire({
      title: 'Export Resell History',
      html: `
        <div class="bg-gradient-to-br from-slate-800 via-slate-900 to-slate-800 p-6 rounded-xl">
          <p class="${theme.tailwind.text9} mb-4">Select the period for export:</p>
          <select 
            id="exportPeriod" 
            class="swal2-select bg-slate-800 ${theme.tailwind.text9} border ${theme.tailwind.border19} rounded-md p-2 outline-none focus:ring-2 focus:ring-teal-500">
            <option value="all" class="bg-slate-800 ${theme.tailwind.text9}">All Time</option>
            <option value="day" class="bg-slate-800 ${theme.tailwind.text9}">Today</option>
            <option value="week" class="bg-slate-800 ${theme.tailwind.text9}">This Week</option>
            <option value="month" class="bg-slate-800 ${theme.tailwind.text9}">This Month</option>
            <option value="year" class="bg-slate-800 ${theme.tailwind.text9}">This Year</option>
          </select>
        </div>
      `,
      confirmButtonText: 'Export',
      focusConfirm: false,
      background: 'bg-gradient-to-br from-slate-800 via-slate-900 to-slate-800',
      customClass: {
        popup: 'bg-gradient-to-br from-slate-800 via-slate-900 to-slate-800',
        title: `${theme.tailwind.text9}`,
        confirmButton:
          'bg-teal-600 hover:bg-teal-700 text-white px-6 py-2 rounded-lg',
        content: 'bg-slate-800 p-6 rounded-lg'
      },
      preConfirm: () => {
        return document.getElementById('exportPeriod').value
      },
      willOpen: () => {
        const style = document.createElement('style')
        style.innerHTML = `
          #exportPeriod {
            color: #81E6D9; 
          }
          #exportPeriod:focus {
            background-color: #1E293B;
          }
        `
        document.head.appendChild(style)
      }
    }).then(result => {
      if (result.isConfirmed) {
        const period = result.value
        const filteredResells = filterTransactionsByPeriod(resells, period)
        exportResellsToExcel(filteredResells, period)
      }
    })
  }

  return (
    <div
      className={`p-8 max-w-7xl mx-auto ${gradient} rounded-2xl shadow-[0_8px_32px_0_rgba(0,0,0,0.36)]`}
    >
      <div className='flex justify-between items-center mb-6'>
        <h2
          className={`text-3xl font-extrabold ${theme.tailwind.text} tracking-tight`}
        >
          {activeSociety?.name || 'Society'} - Resell History
        </h2>
        <div className='flex space-x-3'>
          <button
            onClick={handleExportToExcel}
            className={`${theme.tailwind.bg18
              } text-white px-4 py-2 rounded-lg ${theme.tailwind.bgHover9
              } transition-colors duration-300 flex items-center shadow-md ${filteredResells.length === 0
                ? 'opacity-50 cursor-not-allowed'
                : ''
              }`}
            disabled={filteredResells.length === 0}
          >
            <Download className='mr-2 w-5 h-5' /> Export
          </button>
        </div>
      </div>

      <div className='overflow-x-auto'>
        <table
          className={`w-full border-separate border-spacing-0 ${theme.tailwind.bg2} rounded-2xl overflow-hidden shadow-xl`}
        >
          <thead>
            <tr className={`${theme.tailwind.bg6}`}>
              {[
                { key: 'plot_id.plot_number', label: 'Plot Number' },
                {
                  key: 'previous_customer_id.name',
                  label: 'Previous Customer'
                },
                { key: 'new_customer_id.name', label: 'New Customer' },
                { key: 'seller_fee_amount', label: 'Seller Fee' },
                { key: 'purchaser_fee_amount', label: 'Purchaser Fee' },
                { key: 'plot_id.society_id.name', label: 'Society' },
                { key: 'resell_date', label: 'Resell Date' },
                { key: 'actions', label: 'Actions' }
              ].map(({ key, label }) => (
                <th
                  key={key}
                  className={`px-6 py-4 text-left ${theme.tailwind.text9} font-semibold tracking-wider cursor-pointer hover:bg-slate-800/60 transition-colors duration-200`}
                  onClick={() => key !== 'actions' && handleSort(key)}
                >
                  <div className='flex items-center space-x-2'>
                    <span>{label}</span>
                    {sortColumn === key &&
                      key !== 'actions' &&
                      (sortDirection === 'asc' ? (
                        <ArrowUp className={`w-4 h-4 ${theme.tailwind.text}`} />
                      ) : (
                        <ArrowDown
                          className={`w-4 h-4 ${theme.tailwind.text}`}
                        />
                      ))}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filteredResells.length === 0 ? (
              <tr>
                <td
                  colSpan='8'
                  className={`px-6 py-12 text-center ${theme.tailwind.text9}`}
                >
                  <div className='flex flex-col items-center justify-center space-y-3'>
                    <p className='text-lg font-semibold'>
                      No resell history found
                    </p>
                    <p className={`text-sm ${theme.tailwind.text12}`}>
                      When plots are resold in{' '}
                      {activeSociety?.name || 'this society'}, they will appear
                      here
                    </p>
                  </div>
                </td>
              </tr>
            ) : (
              filteredResells.map((resell, index) => (
                <tr
                  key={resell._id}
                  className={`transition-colors duration-300 ${index % 2 === 0
                    ? `${theme.tailwind.bg2}`
                    : `${theme.tailwind.bg36}`
                    } ${theme.tailwind.bgHover4}`}
                >
                  <td className={`px-6 py-4 ${theme.tailwind.text9}`}>
                    {resell.plot_id?.plot_number || 'N/A'}
                  </td>
                  <td className={`px-6 py-4 ${theme.tailwind.text9}`}>
                    {resell.previous_customer_id?.name || 'N/A'}
                  </td>
                  <td className={`px-6 py-4 ${theme.tailwind.text9}`}>
                    {resell.new_customer_id?.name || 'N/A'}
                  </td>
                  <td className={`px-6 py-4 ${theme.tailwind.text9}`}>
                    {resell.seller_fee_amount && resell.seller_fee_amount > 0
                      ? `PKR ${new Intl.NumberFormat().format(
                        resell.seller_fee_amount
                      )}`
                      : 'N/A'}
                  </td>
                  <td className={`px-6 py-4 ${theme.tailwind.text9}`}>
                    {resell.purchaser_fee_amount && resell.purchaser_fee_amount > 0
                      ? `PKR ${new Intl.NumberFormat().format(
                        resell.purchaser_fee_amount
                      )}`
                      : 'N/A'}
                  </td>
                  <td className={`px-6 py-4 ${theme.tailwind.text9}`}>
                    {resell.plot_id?.society_id?.name || 'N/A'}
                  </td>
                  <td className={`px-6 py-4 ${theme.tailwind.text9}`}>
                    {new Date(resell.resell_date || 'N/A').toLocaleDateString(
                      'en-GB'
                    )}
                  </td>
                  <td className='px-6 py-4 flex space-x-2'>
                    <button
                      onClick={() => handleViewSlip(resell)}
                      className={`${theme.tailwind.bg18} text-white px-3 py-2 rounded-lg ${theme.tailwind.bgHover9} transition-colors duration-300 flex items-center shadow-md`}
                    >
                      <Printer className='w-4 h-4 mr-2' /> View
                    </button>
                    <button
                      onClick={async () => {
                        try {
                          await api.downloadResellSlipPDF(resell._id);
                          enqueueSnackbar('Agreement downloaded successfully', { variant: 'success' });
                        } catch (error) {
                          enqueueSnackbar(error.message || 'Failed to download agreement', { variant: 'error' });
                        }
                      }}
                      className='bg-teal-600 text-white px-3 py-2 rounded-lg hover:bg-teal-500 transition-colors duration-300 flex items-center shadow-md'
                    >
                      <Printer className='w-4 h-4 mr-2' /> Deed
                    </button>
                    {!isEmployee && (
                      <button
                        onClick={() => handleDelete(resell._id)}
                        className='bg-red-600 text-white px-3 py-2 rounded-lg hover:bg-red-500 transition-colors duration-300 flex items-center shadow-md'
                      >
                        <Trash2 className='w-4 h-4' />
                      </button>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      {/* Modal for Slip View */}
      {selectedResell && (
        <>
          {/* Hidden Print Content */}
          <div style={{ display: 'none' }}>
            <div ref={printRef}>
              <div
                style={{
                  width: '210mm',
                  height: '297mm',
                  margin: 0,
                  padding: '20mm 15mm',
                  boxSizing: 'border-box',
                  backgroundColor: 'white',
                  fontFamily: "'Inter', sans-serif",
                  position: 'relative',
                  color: '#1a1a1a'
                }}
              >
                {/* Header Background */}
                <div
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: '90mm',
                    background:
                      'linear-gradient(135deg, #000428 0%, #004e92 100%)',
                    WebkitPrintColorAdjust: 'exact',
                    printColorAdjust: 'exact'
                  }}
                >
                  <div
                    style={{
                      position: 'absolute',
                      bottom: 0,
                      left: 0,
                      right: 0,
                      height: '30mm',
                      background:
                        'linear-gradient(to bottom right, transparent 49.5%, white 50%)'
                    }}
                  ></div>
                </div>

                {/* Content Wrapper */}
                <div style={{ position: 'relative', height: '100%' }}>
                  {/* Header */}
                  <div
                    style={{
                      textAlign: 'center',
                      color: 'white',
                      paddingTop: '10mm',
                      marginBottom: '15mm'
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        gap: '20mm',
                        marginBottom: '5mm'
                      }}
                    >
                      <div
                        style={{
                          width: '30mm',
                          height: '30mm',
                          borderRadius: '50%',
                          overflow: 'hidden',
                          boxShadow: '0 4px 10px rgba(0, 0, 0, 0.3)'
                        }}
                      >
                        <img
                          src={
                            selectedResell.plot_id?.society_id?.society_image ||
                            ''
                          }
                          alt='Logo'
                          style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover'
                          }}
                        />
                      </div>
                      <div>
                        <h1
                          style={{
                            margin: 0,
                            fontSize: '16pt',
                            fontWeight: 900,
                            letterSpacing: '2px',
                            textShadow: '2px 2px 8px rgba(0, 0, 0, 0.5)',
                            color: '#FFD700'
                          }}
                        >
                          {selectedResell.plot_id?.society_id?.name || 'N/A'}
                        </h1>
                        <p
                          style={{
                            margin: '4mm 0 0',
                            fontSize: '14pt',
                            textTransform: 'uppercase',
                            letterSpacing: '1px',
                            color: '#ddd'
                          }}
                        >
                          {selectedResell.plot_id?.society_id?.location ||
                            'N/A'}
                        </p>
                        <p
                          style={{
                            margin: '2mm 0 0',
                            fontSize: '14pt',
                            textTransform: 'uppercase',
                            letterSpacing: '1px',
                            fontStyle: 'italic',
                            color: '#ddd'
                          }}
                        >
                          Resell Plot Slip
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Certificate Details */}
                  <div
                    style={{
                      background: 'white',
                      borderRadius: '5mm',
                      padding: '5mm',
                      boxShadow: '0 2mm 5mm rgba(0,0,0,0.1)',
                      marginBottom: '5mm',
                      pageBreakInside: 'avoid'
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        fontSize: '12pt',
                        fontFamily: 'Arial, sans-serif'
                      }}
                    >
                      <div style={{ flex: 1, textAlign: 'left' }}>
                        <span style={{ color: '#666', fontWeight: 'bold' }}>
                          Certificate No:
                        </span>
                        <span style={{ fontWeight: 300, marginLeft: '5px' }}>
                          RC-
                          {Math.random()
                            .toString(36)
                            .substr(2, 8)
                            .toUpperCase()}
                        </span>
                      </div>
                      <div style={{ flex: 1, textAlign: 'right' }}>
                        <span style={{ color: '#666', fontWeight: 'bold' }}>
                          Date:
                        </span>
                        <span style={{ fontWeight: 300, marginLeft: '5px' }}>
                          {new Date(
                            selectedResell?.resell_date || new Date()
                          ).toLocaleDateString('en-GB')}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Main Content Grid */}
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: '1fr 1fr',
                      gap: '5mm',
                      marginBottom: '5mm'
                    }}
                  >
                    {/* Plot Details */}
                    <div
                      style={{
                        background: 'white',
                        borderRadius: '3mm',
                        padding: '5mm',
                        boxShadow: '0 1mm 3mm rgba(0,0,0,0.1)'
                      }}
                    >
                      <h2
                        style={{
                          margin: '0 0 3mm',
                          fontSize: '14pt',
                          color: '#000428'
                        }}
                      >
                        Plot Information
                      </h2>
                      <table
                        style={{ width: '100%', borderCollapse: 'collapse' }}
                      >
                        <tbody>
                          <tr style={{ borderBottom: '0.3mm solid #eee' }}>
                            <td style={{ padding: '2mm 0', color: '#666' }}>
                              Plot Number:
                            </td>
                            <td
                              style={{
                                padding: '2mm 0',
                                textAlign: 'right',
                                fontWeight: 600
                              }}
                            >
                              {selectedResell.plot_id?.plot_number || 'N/A'}
                            </td>
                          </tr>
                          <tr style={{ borderBottom: '0.3mm solid #eee' }}>
                            <td style={{ padding: '2mm 0', color: '#666' }}>
                              Plot Size:
                            </td>
                            <td
                              style={{
                                padding: '2mm 0',
                                textAlign: 'right',
                                fontWeight: 600
                              }}
                            >
                              {selectedResell.plot_id?.size
                                ? `${selectedResell.plot_id.size} Marla`
                                : 'N/A'}
                            </td>
                          </tr>
                          <tr>
                            <td style={{ padding: '2mm 0', color: '#666' }}>
                              Society:
                            </td>
                            <td
                              style={{
                                padding: '2mm 0',
                                textAlign: 'right',
                                fontWeight: 600
                              }}
                            >
                              {selectedResell.plot_id?.society_id?.name ||
                                'N/A'}
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>

                    {/* Transaction Details */}
                    <div
                      style={{
                        background: 'white',
                        borderRadius: '3mm',
                        padding: '5mm',
                        boxShadow: '0 1mm 3mm rgba(0,0,0,0.1)'
                      }}
                    >
                      <h2
                        style={{
                          margin: '0 0 3mm',
                          fontSize: '14pt',
                          color: '#000428'
                        }}
                      >
                        Transaction Details
                      </h2>
                      <table
                        style={{ width: '100%', borderCollapse: 'collapse' }}
                      >
                        <tbody>
                          <tr style={{ borderBottom: '0.3mm solid #eee' }}>
                            <td style={{ padding: '2mm 0', color: '#666' }}>
                              Previous Owner:
                            </td>
                            <td
                              style={{
                                padding: '2mm 0',
                                textAlign: 'right',
                                fontWeight: 600
                              }}
                            >
                              {selectedResell.previous_customer_id?.name ||
                                'N/A'}
                            </td>
                          </tr>
                          <tr style={{ borderBottom: '0.3mm solid #eee' }}>
                            <td style={{ padding: '2mm 0', color: '#666' }}>
                              New Owner:
                            </td>
                            <td
                              style={{
                                padding: '2mm 0',
                                textAlign: 'right',
                                fontWeight: 600
                              }}
                            >
                              {selectedResell.new_customer_id?.name || 'N/A'}
                            </td>
                          </tr>
                          {selectedResell.seller_fee_amount > 0 && (
                            <tr style={{ borderBottom: '0.3mm solid #eee' }}>
                              <td style={{ padding: '2mm 0', color: '#666' }}>
                                Seller Fee:
                              </td>
                              <td
                                style={{
                                  padding: '2mm 0',
                                  textAlign: 'right',
                                  fontWeight: 600
                                }}
                              >
                                {selectedResell.seller_fee_amount
                                  ? `PKR ${selectedResell.seller_fee_amount.toLocaleString()}`
                                  : 'N/A'}
                              </td>
                            </tr>
                          )}
                          {selectedResell.purchaser_fee_amount > 0 && (
                            <tr>
                              <td style={{ padding: '2mm 0', color: '#666' }}>
                                Purchaser Fee:
                              </td>
                              <td
                                style={{
                                  padding: '2mm 0',
                                  textAlign: 'right',
                                  fontWeight: 600
                                }}
                              >
                                {selectedResell.purchaser_fee_amount
                                  ? `PKR ${selectedResell.purchaser_fee_amount.toLocaleString()}`
                                  : 'N/A'}
                              </td>
                            </tr>
                          )}
                          {(selectedResell.seller_fee_amount > 0 || selectedResell.purchaser_fee_amount > 0) && (
                            <tr style={{ borderTop: '0.5mm solid #000', marginTop: '2mm' }}>
                              <td style={{ padding: '2mm 0', color: '#000', fontWeight: 'bold' }}>
                                Total Fee:
                              </td>
                              <td
                                style={{
                                  padding: '2mm 0',
                                  textAlign: 'right',
                                  fontWeight: 700,
                                  color: '#000428'
                                }}
                              >
                                {selectedResell.resell_fee
                                  ? `PKR ${selectedResell.resell_fee.toLocaleString()}`
                                  : 'N/A'}
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* Terms */}
                  <div
                    style={{
                      background: 'white',
                      borderRadius: '3mm',
                      padding: '5mm',
                      boxShadow: '0 1mm 3mm rgba(0,0,0,0.1)',
                      marginBottom: '5mm',
                      pageBreakInside: 'avoid'
                    }}
                  >
                    <h2
                      style={{
                        margin: '0 0 3mm',
                        fontSize: '14pt',
                        color: '#000428',
                        fontFamily: 'Arial, sans-serif'
                      }}
                    >
                      Terms & Conditions
                    </h2>
                    <ol
                      style={{
                        margin: 0,
                        paddingLeft: '1.5em',
                        color: '#444',
                        fontSize: '9pt',
                        lineHeight: 1.8,
                        fontFamily: 'Arial, sans-serif',
                        listStyleType: 'decimal'
                      }}
                    >
                      <li
                        style={{
                          marginBottom: '5px',
                          textAlign: 'justify',
                          wordWrap: 'break-word'
                        }}
                      >
                        The transfer is final and legally binding upon
                        signatures and payment.
                      </li>
                      <li
                        style={{
                          marginBottom: '5px',
                          textAlign: 'justify',
                          wordWrap: 'break-word'
                        }}
                      >
                        The new owner accepts the property as-is and assumes all
                        responsibilities.
                      </li>
                      <li
                        style={{
                          marginBottom: '5px',
                          textAlign: 'justify',
                          wordWrap: 'break-word'
                        }}
                      >
                        Any outstanding dues, taxes, or charges must be cleared
                        before transfer completion.
                      </li>
                      <li
                        style={{
                          marginBottom: 0,
                          textAlign: 'justify',
                          wordWrap: 'break-word'
                        }}
                      >
                        This certificate is only valid with authorized
                        signatures and the official society stamp.
                      </li>
                    </ol>
                  </div>

                  {/* Signatures */}
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(3, 1fr)',
                      gap: '5mm',
                      marginTop: '10mm'
                    }}
                  >
                    <div style={{ textAlign: 'center' }}>
                      <div
                        style={{
                          borderBottom: '0.5mm solid #000',
                          height: '15mm',
                          marginBottom: '2mm'
                        }}
                      ></div>
                      <p
                        style={{ margin: 0, fontSize: '10pt', fontWeight: 600 }}
                      >
                        Previous Owner
                      </p>
                      <p
                        style={{
                          margin: '1mm 0 0',
                          fontSize: '8pt',
                          color: '#666'
                        }}
                      >
                        Signature & Date
                      </p>
                    </div>
                    <div style={{ textAlign: 'center' }}>
                      <div
                        style={{
                          borderBottom: '0.5mm solid #000',
                          height: '15mm',
                          marginBottom: '2mm'
                        }}
                      ></div>
                      <p
                        style={{ margin: 0, fontSize: '10pt', fontWeight: 600 }}
                      >
                        New Owner
                      </p>
                      <p
                        style={{
                          margin: '1mm 0 0',
                          fontSize: '8pt',
                          color: '#666'
                        }}
                      >
                        Signature & Date
                      </p>
                    </div>
                    <div style={{ textAlign: 'center' }}>
                      <div
                        style={{
                          borderBottom: '0.5mm solid #000',
                          height: '15mm',
                          marginBottom: '2mm'
                        }}
                      ></div>
                      <p
                        style={{ margin: 0, fontSize: '10pt', fontWeight: 600 }}
                      >
                        Society Representative
                      </p>
                      <p
                        style={{
                          margin: '1mm 0 0',
                          fontSize: '8pt',
                          color: '#666'
                        }}
                      >
                        Signature & Date
                      </p>
                      <div style={{ marginTop: '5mm' }}>
                        <div
                          style={{
                            width: '25mm',
                            height: '25mm',
                            border: '0.5mm dashed #000',
                            borderRadius: '50%',
                            margin: '0 auto',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                        >
                          <span style={{ fontSize: '8pt', color: '#666' }}>
                            Official Stamp
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Hidden Seller Fee Print Content */}
          <div style={{ display: 'none' }}>
            <div ref={sellerPrintRef}>
              <div
                style={{
                  width: '210mm',
                  height: '297mm',
                  margin: 0,
                  padding: '20mm 15mm',
                  boxSizing: 'border-box',
                  backgroundColor: 'white',
                  fontFamily: "'Inter', sans-serif",
                  position: 'relative',
                  color: '#1a1a1a'
                }}
              >
                {/* Header Background */}
                <div
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: '90mm',
                    background:
                      'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)',
                    WebkitPrintColorAdjust: 'exact',
                    printColorAdjust: 'exact'
                  }}
                >
                  <div
                    style={{
                      position: 'absolute',
                      bottom: 0,
                      left: 0,
                      right: 0,
                      height: '30mm',
                      background:
                        'linear-gradient(to bottom right, transparent 49.5%, white 50%)'
                    }}
                  ></div>
                </div>

                {/* Content Wrapper */}
                <div style={{ position: 'relative', height: '100%' }}>
                  {/* Header */}
                  <div
                    style={{
                      textAlign: 'center',
                      color: 'white',
                      paddingTop: '10mm',
                      marginBottom: '15mm'
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        gap: '20mm',
                        marginBottom: '5mm'
                      }}
                    >
                      <div
                        style={{
                          width: '30mm',
                          height: '30mm',
                          borderRadius: '50%',
                          overflow: 'hidden',
                          boxShadow: '0 4px 10px rgba(0, 0, 0, 0.3)'
                        }}
                      >
                        <img
                          src={
                            selectedResell.plot_id?.society_id?.society_image ||
                            ''
                          }
                          alt='Logo'
                          style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover'
                          }}
                        />
                      </div>
                      <div>
                        <h1
                          style={{
                            margin: 0,
                            fontSize: '16pt',
                            fontWeight: 900,
                            letterSpacing: '2px',
                            textShadow: '2px 2px 8px rgba(0, 0, 0, 0.5)',
                            color: '#FFD700'
                          }}
                        >
                          {selectedResell.plot_id?.society_id?.name || 'N/A'}
                        </h1>
                        <p
                          style={{
                            margin: '4mm 0 0',
                            fontSize: '14pt',
                            textTransform: 'uppercase',
                            letterSpacing: '1px',
                            color: '#ddd'
                          }}
                        >
                          {selectedResell.plot_id?.society_id?.location ||
                            'N/A'}
                        </p>
                        <p
                          style={{
                            margin: '2mm 0 0',
                            fontSize: '14pt',
                            textTransform: 'uppercase',
                            letterSpacing: '1px',
                            fontStyle: 'italic',
                            color: '#ddd'
                          }}
                        >
                          Seller Fee Certificate
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Certificate Details */}
                  <div
                    style={{
                      background: 'white',
                      borderRadius: '5mm',
                      padding: '5mm',
                      boxShadow: '0 2mm 5mm rgba(0,0,0,0.1)',
                      marginBottom: '5mm',
                      pageBreakInside: 'avoid'
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        fontSize: '12pt',
                        fontFamily: 'Arial, sans-serif'
                      }}
                    >
                      <div style={{ flex: 1, textAlign: 'left' }}>
                        <span style={{ color: '#666', fontWeight: 'bold' }}>
                          Certificate No:
                        </span>
                        <span style={{ fontWeight: 300, marginLeft: '5px' }}>
                          SFC-
                          {Math.random()
                            .toString(36)
                            .substr(2, 8)
                            .toUpperCase()}
                        </span>
                      </div>
                      <div style={{ flex: 1, textAlign: 'right' }}>
                        <span style={{ color: '#666', fontWeight: 'bold' }}>
                          Date:
                        </span>
                        <span style={{ fontWeight: 300, marginLeft: '5px' }}>
                          {new Date(
                            selectedResell?.resell_date || new Date()
                          ).toLocaleDateString('en-GB')}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Main Content Grid */}
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: '1fr 1fr',
                      gap: '5mm',
                      marginBottom: '5mm'
                    }}
                  >
                    {/* Plot Details */}
                    <div
                      style={{
                        background: 'white',
                        borderRadius: '3mm',
                        padding: '5mm',
                        boxShadow: '0 1mm 3mm rgba(0,0,0,0.1)'
                      }}
                    >
                      <h2
                        style={{
                          margin: '0 0 3mm',
                          fontSize: '14pt',
                          color: '#1e3a8a'
                        }}
                      >
                        Plot Information
                      </h2>
                      <table
                        style={{ width: '100%', borderCollapse: 'collapse' }}
                      >
                        <tbody>
                          <tr style={{ borderBottom: '0.3mm solid #eee' }}>
                            <td style={{ padding: '2mm 0', color: '#666' }}>
                              Plot Number:
                            </td>
                            <td
                              style={{
                                padding: '2mm 0',
                                textAlign: 'right',
                                fontWeight: 600
                              }}
                            >
                              {selectedResell.plot_id?.plot_number || 'N/A'}
                            </td>
                          </tr>
                          <tr style={{ borderBottom: '0.3mm solid #eee' }}>
                            <td style={{ padding: '2mm 0', color: '#666' }}>
                              Plot Size:
                            </td>
                            <td
                              style={{
                                padding: '2mm 0',
                                textAlign: 'right',
                                fontWeight: 600
                              }}
                            >
                              {selectedResell.plot_id?.size
                                ? `${selectedResell.plot_id.size} Marla`
                                : 'N/A'}
                            </td>
                          </tr>
                          <tr>
                            <td style={{ padding: '2mm 0', color: '#666' }}>
                              Society:
                            </td>
                            <td
                              style={{
                                padding: '2mm 0',
                                textAlign: 'right',
                                fontWeight: 600
                              }}
                            >
                              {selectedResell.plot_id?.society_id?.name ||
                                'N/A'}
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>

                    {/* Seller Fee Details */}
                    <div
                      style={{
                        background: 'white',
                        borderRadius: '3mm',
                        padding: '5mm',
                        boxShadow: '0 1mm 3mm rgba(0,0,0,0.1)'
                      }}
                    >
                      <h2
                        style={{
                          margin: '0 0 3mm',
                          fontSize: '14pt',
                          color: '#1e3a8a'
                        }}
                      >
                        Seller Fee Details
                      </h2>
                      <table
                        style={{ width: '100%', borderCollapse: 'collapse' }}
                      >
                        <tbody>
                          <tr style={{ borderBottom: '0.3mm solid #eee' }}>
                            <td style={{ padding: '2mm 0', color: '#666' }}>
                              Seller Name:
                            </td>
                            <td
                              style={{
                                padding: '2mm 0',
                                textAlign: 'right',
                                fontWeight: 600
                              }}
                            >
                              {selectedResell.previous_customer_id?.name ||
                                'N/A'}
                            </td>
                          </tr>
                          <tr style={{ borderBottom: '0.3mm solid #eee' }}>
                            <td style={{ padding: '2mm 0', color: '#666' }}>
                              Fee Percentage:
                            </td>
                            <td
                              style={{
                                padding: '2mm 0',
                                textAlign: 'right',
                                fontWeight: 600
                              }}
                            >
                              {selectedResell.seller_fee_percentage
                                ? `${selectedResell.seller_fee_percentage}%`
                                : 'N/A'}
                            </td>
                          </tr>
                          <tr>
                            <td style={{ padding: '2mm 0', color: '#666', fontWeight: 'bold' }}>
                              Fee Amount:
                            </td>
                            <td
                              style={{
                                padding: '2mm 0',
                                textAlign: 'right',
                                fontWeight: 700,
                                color: '#1e3a8a',
                                fontSize: '14pt'
                              }}
                            >
                              {selectedResell.seller_fee_amount
                                ? `PKR ${selectedResell.seller_fee_amount.toLocaleString()}`
                                : 'N/A'}
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* Terms */}
                  <div
                    style={{
                      background: 'white',
                      borderRadius: '3mm',
                      padding: '5mm',
                      boxShadow: '0 1mm 3mm rgba(0,0,0,0.1)',
                      marginBottom: '5mm',
                      pageBreakInside: 'avoid'
                    }}
                  >
                    <h2
                      style={{
                        margin: '0 0 3mm',
                        fontSize: '14pt',
                        color: '#1e3a8a',
                        fontFamily: 'Arial, sans-serif'
                      }}
                    >
                      Terms & Conditions
                    </h2>
                    <ol
                      style={{
                        margin: 0,
                        paddingLeft: '1.5em',
                        color: '#444',
                        fontSize: '9pt',
                        lineHeight: 1.8,
                        fontFamily: 'Arial, sans-serif',
                        listStyleType: 'decimal'
                      }}
                    >
                      <li
                        style={{
                          marginBottom: '5px',
                          textAlign: 'justify',
                          wordWrap: 'break-word'
                        }}
                      >
                        This certificate confirms the seller fee payment for the plot resell transaction.
                      </li>
                      <li
                        style={{
                          marginBottom: '5px',
                          textAlign: 'justify',
                          wordWrap: 'break-word'
                        }}
                      >
                        The seller fee is calculated as a percentage of the total plot amount.
                      </li>
                      <li
                        style={{
                          marginBottom: '5px',
                          textAlign: 'justify',
                          wordWrap: 'break-word'
                        }}
                      >
                        This fee is recorded as income in the financial records.
                      </li>
                      <li
                        style={{
                          marginBottom: 0,
                          textAlign: 'justify',
                          wordWrap: 'break-word'
                        }}
                      >
                        This certificate is only valid with authorized signatures and the official society stamp.
                      </li>
                    </ol>
                  </div>

                  {/* Signatures */}
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(2, 1fr)',
                      gap: '5mm',
                      marginTop: '10mm'
                    }}
                  >
                    <div style={{ textAlign: 'center' }}>
                      <div
                        style={{
                          borderBottom: '0.5mm solid #000',
                          height: '15mm',
                          marginBottom: '2mm'
                        }}
                      ></div>
                      <p
                        style={{ margin: 0, fontSize: '10pt', fontWeight: 600 }}
                      >
                        Seller
                      </p>
                      <p
                        style={{
                          margin: '1mm 0 0',
                          fontSize: '8pt',
                          color: '#666'
                        }}
                      >
                        Signature & Date
                      </p>
                    </div>
                    <div style={{ textAlign: 'center' }}>
                      <div
                        style={{
                          borderBottom: '0.5mm solid #000',
                          height: '15mm',
                          marginBottom: '2mm'
                        }}
                      ></div>
                      <p
                        style={{ margin: 0, fontSize: '10pt', fontWeight: 600 }}
                      >
                        Society Representative
                      </p>
                      <p
                        style={{
                          margin: '1mm 0 0',
                          fontSize: '8pt',
                          color: '#666'
                        }}
                      >
                        Signature & Date
                      </p>
                      <div style={{ marginTop: '5mm' }}>
                        <div
                          style={{
                            width: '25mm',
                            height: '25mm',
                            border: '0.5mm dashed #000',
                            borderRadius: '50%',
                            margin: '0 auto',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                        >
                          <span style={{ fontSize: '8pt', color: '#666' }}>
                            Official Stamp
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Hidden Purchaser Fee Print Content */}
          <div style={{ display: 'none' }}>
            <div ref={purchaserPrintRef}>
              <div
                style={{
                  width: '210mm',
                  height: '297mm',
                  margin: 0,
                  padding: '20mm 15mm',
                  boxSizing: 'border-box',
                  backgroundColor: 'white',
                  fontFamily: "'Inter', sans-serif",
                  position: 'relative',
                  color: '#1a1a1a'
                }}
              >
                {/* Header Background */}
                <div
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: '90mm',
                    background:
                      'linear-gradient(135deg, #065f46 0%, #10b981 100%)',
                    WebkitPrintColorAdjust: 'exact',
                    printColorAdjust: 'exact'
                  }}
                >
                  <div
                    style={{
                      position: 'absolute',
                      bottom: 0,
                      left: 0,
                      right: 0,
                      height: '30mm',
                      background:
                        'linear-gradient(to bottom right, transparent 49.5%, white 50%)'
                    }}
                  ></div>
                </div>

                {/* Content Wrapper */}
                <div style={{ position: 'relative', height: '100%' }}>
                  {/* Header */}
                  <div
                    style={{
                      textAlign: 'center',
                      color: 'white',
                      paddingTop: '10mm',
                      marginBottom: '15mm'
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        gap: '20mm',
                        marginBottom: '5mm'
                      }}
                    >
                      <div
                        style={{
                          width: '30mm',
                          height: '30mm',
                          borderRadius: '50%',
                          overflow: 'hidden',
                          boxShadow: '0 4px 10px rgba(0, 0, 0, 0.3)'
                        }}
                      >
                        <img
                          src={
                            selectedResell.plot_id?.society_id?.society_image ||
                            ''
                          }
                          alt='Logo'
                          style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover'
                          }}
                        />
                      </div>
                      <div>
                        <h1
                          style={{
                            margin: 0,
                            fontSize: '16pt',
                            fontWeight: 900,
                            letterSpacing: '2px',
                            textShadow: '2px 2px 8px rgba(0, 0, 0, 0.5)',
                            color: '#FFD700'
                          }}
                        >
                          {selectedResell.plot_id?.society_id?.name || 'N/A'}
                        </h1>
                        <p
                          style={{
                            margin: '4mm 0 0',
                            fontSize: '14pt',
                            textTransform: 'uppercase',
                            letterSpacing: '1px',
                            color: '#ddd'
                          }}
                        >
                          {selectedResell.plot_id?.society_id?.location ||
                            'N/A'}
                        </p>
                        <p
                          style={{
                            margin: '2mm 0 0',
                            fontSize: '14pt',
                            textTransform: 'uppercase',
                            letterSpacing: '1px',
                            fontStyle: 'italic',
                            color: '#ddd'
                          }}
                        >
                          Purchaser Fee Certificate
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Certificate Details */}
                  <div
                    style={{
                      background: 'white',
                      borderRadius: '5mm',
                      padding: '5mm',
                      boxShadow: '0 2mm 5mm rgba(0,0,0,0.1)',
                      marginBottom: '5mm',
                      pageBreakInside: 'avoid'
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        fontSize: '12pt',
                        fontFamily: 'Arial, sans-serif'
                      }}
                    >
                      <div style={{ flex: 1, textAlign: 'left' }}>
                        <span style={{ color: '#666', fontWeight: 'bold' }}>
                          Certificate No:
                        </span>
                        <span style={{ fontWeight: 300, marginLeft: '5px' }}>
                          PFC-
                          {Math.random()
                            .toString(36)
                            .substr(2, 8)
                            .toUpperCase()}
                        </span>
                      </div>
                      <div style={{ flex: 1, textAlign: 'right' }}>
                        <span style={{ color: '#666', fontWeight: 'bold' }}>
                          Date:
                        </span>
                        <span style={{ fontWeight: 300, marginLeft: '5px' }}>
                          {new Date(
                            selectedResell?.resell_date || new Date()
                          ).toLocaleDateString('en-GB')}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Main Content Grid */}
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: '1fr 1fr',
                      gap: '5mm',
                      marginBottom: '5mm'
                    }}
                  >
                    {/* Plot Details */}
                    <div
                      style={{
                        background: 'white',
                        borderRadius: '3mm',
                        padding: '5mm',
                        boxShadow: '0 1mm 3mm rgba(0,0,0,0.1)'
                      }}
                    >
                      <h2
                        style={{
                          margin: '0 0 3mm',
                          fontSize: '14pt',
                          color: '#065f46'
                        }}
                      >
                        Plot Information
                      </h2>
                      <table
                        style={{ width: '100%', borderCollapse: 'collapse' }}
                      >
                        <tbody>
                          <tr style={{ borderBottom: '0.3mm solid #eee' }}>
                            <td style={{ padding: '2mm 0', color: '#666' }}>
                              Plot Number:
                            </td>
                            <td
                              style={{
                                padding: '2mm 0',
                                textAlign: 'right',
                                fontWeight: 600
                              }}
                            >
                              {selectedResell.plot_id?.plot_number || 'N/A'}
                            </td>
                          </tr>
                          <tr style={{ borderBottom: '0.3mm solid #eee' }}>
                            <td style={{ padding: '2mm 0', color: '#666' }}>
                              Plot Size:
                            </td>
                            <td
                              style={{
                                padding: '2mm 0',
                                textAlign: 'right',
                                fontWeight: 600
                              }}
                            >
                              {selectedResell.plot_id?.size
                                ? `${selectedResell.plot_id.size} Marla`
                                : 'N/A'}
                            </td>
                          </tr>
                          <tr>
                            <td style={{ padding: '2mm 0', color: '#666' }}>
                              Society:
                            </td>
                            <td
                              style={{
                                padding: '2mm 0',
                                textAlign: 'right',
                                fontWeight: 600
                              }}
                            >
                              {selectedResell.plot_id?.society_id?.name ||
                                'N/A'}
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>

                    {/* Purchaser Fee Details */}
                    <div
                      style={{
                        background: 'white',
                        borderRadius: '3mm',
                        padding: '5mm',
                        boxShadow: '0 1mm 3mm rgba(0,0,0,0.1)'
                      }}
                    >
                      <h2
                        style={{
                          margin: '0 0 3mm',
                          fontSize: '14pt',
                          color: '#065f46'
                        }}
                      >
                        Purchaser Fee Details
                      </h2>
                      <table
                        style={{ width: '100%', borderCollapse: 'collapse' }}
                      >
                        <tbody>
                          <tr style={{ borderBottom: '0.3mm solid #eee' }}>
                            <td style={{ padding: '2mm 0', color: '#666' }}>
                              Purchaser Name:
                            </td>
                            <td
                              style={{
                                padding: '2mm 0',
                                textAlign: 'right',
                                fontWeight: 600
                              }}
                            >
                              {selectedResell.new_customer_id?.name || 'N/A'}
                            </td>
                          </tr>
                          <tr style={{ borderBottom: '0.3mm solid #eee' }}>
                            <td style={{ padding: '2mm 0', color: '#666' }}>
                              Fee Percentage:
                            </td>
                            <td
                              style={{
                                padding: '2mm 0',
                                textAlign: 'right',
                                fontWeight: 600
                              }}
                            >
                              {selectedResell.purchaser_fee_percentage
                                ? `${selectedResell.purchaser_fee_percentage}%`
                                : 'N/A'}
                            </td>
                          </tr>
                          <tr>
                            <td style={{ padding: '2mm 0', color: '#666', fontWeight: 'bold' }}>
                              Fee Amount:
                            </td>
                            <td
                              style={{
                                padding: '2mm 0',
                                textAlign: 'right',
                                fontWeight: 700,
                                color: '#065f46',
                                fontSize: '14pt'
                              }}
                            >
                              {selectedResell.purchaser_fee_amount
                                ? `PKR ${selectedResell.purchaser_fee_amount.toLocaleString()}`
                                : 'N/A'}
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* Terms */}
                  <div
                    style={{
                      background: 'white',
                      borderRadius: '3mm',
                      padding: '5mm',
                      boxShadow: '0 1mm 3mm rgba(0,0,0,0.1)',
                      marginBottom: '5mm',
                      pageBreakInside: 'avoid'
                    }}
                  >
                    <h2
                      style={{
                        margin: '0 0 3mm',
                        fontSize: '14pt',
                        color: '#065f46',
                        fontFamily: 'Arial, sans-serif'
                      }}
                    >
                      Terms & Conditions
                    </h2>
                    <ol
                      style={{
                        margin: 0,
                        paddingLeft: '1.5em',
                        color: '#444',
                        fontSize: '9pt',
                        lineHeight: 1.8,
                        fontFamily: 'Arial, sans-serif',
                        listStyleType: 'decimal'
                      }}
                    >
                      <li
                        style={{
                          marginBottom: '5px',
                          textAlign: 'justify',
                          wordWrap: 'break-word'
                        }}
                      >
                        This certificate confirms the purchaser fee payment for the plot resell transaction.
                      </li>
                      <li
                        style={{
                          marginBottom: '5px',
                          textAlign: 'justify',
                          wordWrap: 'break-word'
                        }}
                      >
                        The purchaser fee is calculated as a percentage of the total plot amount.
                      </li>
                      <li
                        style={{
                          marginBottom: '5px',
                          textAlign: 'justify',
                          wordWrap: 'break-word'
                        }}
                      >
                        This fee is recorded as income in the financial records.
                      </li>
                      <li
                        style={{
                          marginBottom: 0,
                          textAlign: 'justify',
                          wordWrap: 'break-word'
                        }}
                      >
                        This certificate is only valid with authorized signatures and the official society stamp.
                      </li>
                    </ol>
                  </div>

                  {/* Signatures */}
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(2, 1fr)',
                      gap: '5mm',
                      marginTop: '10mm'
                    }}
                  >
                    <div style={{ textAlign: 'center' }}>
                      <div
                        style={{
                          borderBottom: '0.5mm solid #000',
                          height: '15mm',
                          marginBottom: '2mm'
                        }}
                      ></div>
                      <p
                        style={{ margin: 0, fontSize: '10pt', fontWeight: 600 }}
                      >
                        Purchaser
                      </p>
                      <p
                        style={{
                          margin: '1mm 0 0',
                          fontSize: '8pt',
                          color: '#666'
                        }}
                      >
                        Signature & Date
                      </p>
                    </div>
                    <div style={{ textAlign: 'center' }}>
                      <div
                        style={{
                          borderBottom: '0.5mm solid #000',
                          height: '15mm',
                          marginBottom: '2mm'
                        }}
                      ></div>
                      <p
                        style={{ margin: 0, fontSize: '10pt', fontWeight: 600 }}
                      >
                        Society Representative
                      </p>
                      <p
                        style={{
                          margin: '1mm 0 0',
                          fontSize: '8pt',
                          color: '#666'
                        }}
                      >
                        Signature & Date
                      </p>
                      <div style={{ marginTop: '5mm' }}>
                        <div
                          style={{
                            width: '25mm',
                            height: '25mm',
                            border: '0.5mm dashed #000',
                            borderRadius: '50%',
                            margin: '0 auto',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                        >
                          <span style={{ fontSize: '8pt', color: '#666' }}>
                            Official Stamp
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Visible Modal */}
          <div className='fixed inset-0 z-[9999] flex items-center justify-center p-3 bg-black/70 backdrop-blur-lg overflow-y-auto'>
            <div
              className={`${gradient} rounded-xl w-full max-w-5xl my-2 border ${theme.tailwind.border19} shadow-2xl`}
            >
              {/* Slip Header - Compact */}
              <div
                className={`${theme.tailwind.bg37} px-6 py-4 border-b backdrop-blur-xl bg-slate-900/30 border-teal-400/30 flex items-center justify-between`}
              >
                <div className='flex items-center gap-3'>
                  <div
                    className={`w-16 h-16 ${theme.tailwind.bg11} rounded-lg border-2 ${theme.tailwind.border7} flex items-center justify-center shadow-lg flex-shrink-0`}
                  >
                    <img
                      src={selectedResell.plot_id?.society_id?.society_image}
                      alt='Company Logo'
                      className='w-14 h-14 object-cover rounded-md'
                    />
                  </div>
                  <div>
                    <h1
                      className={`text-2xl font-bold ${theme.tailwind.text9}`}
                    >
                      {selectedResell.plot_id?.society_id?.name || 'N/A'}
                    </h1>
                    <p className={`${theme.tailwind.text} text-sm`}>
                      {selectedResell.plot_id?.society_id?.location || 'N/A'}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setSelectedResell(null)}
                  className='p-1.5 rounded-lg hover:bg-red-600/20 transition-colors flex-shrink-0'
                >
                  <X className={`w-5 h-5 ${theme.tailwind.text9}`} />
                </button>
              </div>

              {/* Slip Details - Compact Grid */}
              <div className='p-4 max-h-[55vh] overflow-y-auto'>
                <div className='grid grid-cols-2 sm:grid-cols-3 gap-2'>
                  {[
                    {
                      label: 'Plot Number',
                      value: selectedResell.plot_id?.plot_number || 'N/A',
                      Icon: Home
                    },
                    {
                      label: 'Plot Size',
                      value: selectedResell.plot_id?.size
                        ? `${selectedResell.plot_id.size} Marla`
                        : 'N/A',
                      Icon: Ruler
                    },
                    {
                      label: 'Previous Owner',
                      value: selectedResell.previous_customer_id?.name || 'N/A',
                      Icon: User
                    },
                    {
                      label: 'New Owner',
                      value: selectedResell.new_customer_id?.name || 'N/A',
                      Icon: User
                    },
                    {
                      label: 'Seller Fee',
                      value: selectedResell.seller_fee_amount && selectedResell.seller_fee_amount > 0
                        ? `PKR ${new Intl.NumberFormat().format(
                          selectedResell.seller_fee_amount
                        )}`
                        : 'N/A',
                      Icon: DollarSign
                    },
                    {
                      label: 'Purchaser Fee',
                      value: selectedResell.purchaser_fee_amount && selectedResell.purchaser_fee_amount > 0
                        ? `PKR ${new Intl.NumberFormat().format(
                          selectedResell.purchaser_fee_amount
                        )}`
                        : 'N/A',
                      Icon: DollarSign
                    },
                    {
                      label: 'Total Fee',
                      value: selectedResell.resell_fee
                        ? `PKR ${new Intl.NumberFormat().format(
                          selectedResell.resell_fee
                        )}`
                        : 'N/A',
                      Icon: DollarSign
                    },
                    {
                      label: 'Resell Date',
                      value: new Date(
                        selectedResell.resell_date || new Date()
                      ).toLocaleDateString('en-GB'),
                      Icon: Calendar
                    }
                  ].map(({ label, value, Icon }) => (
                    <div
                      key={label}
                      className='bg-white/10 backdrop-blur-2xl rounded-lg p-3 border border-white/20 transition-all duration-300 hover:shadow-xl hover:bg-white/15 hover:border-teal-400/60 shadow-lg'
                    >
                      <div className='flex items-start gap-2 mb-2'>
                        <Icon className='w-4 h-4 flex-shrink-0 text-teal-400 mt-0.5' />
                        <div className='text-white font-semibold text-xs uppercase tracking-tight'>
                          {label}
                        </div>
                      </div>
                      <div className='text-white font-bold text-sm break-words'>
                        {value}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Description Section - Compact */}
                {selectedResell.description && (
                  <div className='mt-3 bg-white/10 backdrop-blur-2xl border border-white/20 rounded-lg p-3 shadow-lg hover:bg-white/15 hover:border-teal-400/60 transition-all duration-300'>
                    <div className='flex items-center gap-2 mb-2'>
                      <FileText className='w-4 h-4 text-teal-400 flex-shrink-0' />
                      <div className='text-white font-semibold text-xs uppercase tracking-tight'>
                        Description
                      </div>
                    </div>
                    <div className='text-white text-sm leading-relaxed line-clamp-3'>
                      {selectedResell.description}
                    </div>
                  </div>
                )}
              </div>

              {/* Slip Actions - Compact */}
              <div
                className={`px-4 py-3 border-t ${theme.tailwind.border19} flex justify-between items-center gap-2 ${theme.tailwind.bg37}`}
              >
                <div className='flex gap-2'>
                  {selectedResell.seller_fee_amount > 0 && (
                    <button
                      onClick={() => {
                        setPrintType('seller')
                        setTimeout(() => handlePrintSellerSlip(), 100)
                      }}
                      className={`bg-blue-600/90 hover:bg-blue-600 text-white px-4 py-2 rounded-lg transition-all duration-300 flex items-center justify-center shadow-md hover:shadow-lg font-semibold text-sm hover:scale-105`}
                    >
                      <Printer className='w-4 h-4 mr-1.5' /> Seller Slip
                    </button>
                  )}
                  {selectedResell.purchaser_fee_amount > 0 && (
                    <button
                      onClick={() => {
                        setPrintType('purchaser')
                        setTimeout(() => handlePrintPurchaserSlip(), 100)
                      }}
                      className={`bg-green-600/90 hover:bg-green-600 text-white px-4 py-2 rounded-lg transition-all duration-300 flex items-center justify-center shadow-md hover:shadow-lg font-semibold text-sm hover:scale-105`}
                    >
                      <Printer className='w-4 h-4 mr-1.5' /> Purchaser Slip
                    </button>
                  )}
                </div>
                <div className='flex gap-2'>
                  <button
                    onClick={handlePrintSlip}
                    className={`${theme.tailwind.bg38} ${theme.tailwind.text9} px-4 py-2 rounded-lg ${theme.tailwind.bgHover5} transition-all duration-300 flex items-center justify-center shadow-md hover:shadow-lg font-semibold text-sm hover:scale-105`}
                  >
                    <Printer className='w-4 h-4 mr-1.5' /> Full Slip
                  </button>

                  <button
                    onClick={() => setSelectedResell(null)}
                    className='bg-red-600/90 hover:bg-red-600 text-white px-4 py-2 rounded-lg transition-all duration-300 flex items-center justify-center shadow-md hover:shadow-lg font-semibold text-sm hover:scale-105'
                  >
                    <X className='w-4 h-4 mr-1.5' /> Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  )
}

export default ResellPlotsList
